import React from 'react'
import styled from '@emotion/styled'
import { css } from '@emotion/core'
import { array, string } from 'prop-types'

import { BestPicks } from './BestPicks'
import { IndividualBestPicks } from './IndividualBestPicks'
import { Pricing } from './Pricing'
import { Reviews } from './Reviews'
import { ContentContainer, Guides } from '@jelly/components'

import {
	SECTION_BEST_PICKS,
	SECTION_BUYING_GUIDE,
	SECTION_CONTAINER,
	SECTION_MORECOVERAGE,
	SECTION_OURAPPROACH,
	SECTION_REVIEWS,
	SECTION_WIDGET,
} from '../constants'

/**
 * Main switch function that returns a sectional component based on type
 *
 * @param {String} categoryWidgetId rfq category widget id set on category level
 * @param {Object} catId BZ category ID to be packed into injected props
 * @param {string} guid The guid of the section page
 * @param {Object} services JSON data decribing the services available
 * @param {Object} section JSON data decribing section
 * @param {string} ip The ip address of the end user
 *
 * @return {Node} React component to render if applicable, null otherwise
 */
const renderSection = ({ catTitle, categoryWidgetId, section, services, guid }) => {
	switch (section.type) {
		case SECTION_BEST_PICKS:
			return <BestPicks {...section} categoryWidgetId={categoryWidgetId} servicesData={services} />
		case SECTION_CONTAINER:
			return <ContentContainer content={section.container.html} />
		case 'guides':
			return <Guides {...section} />
		case 'individualBestPicks':
			return <IndividualBestPicks {...section} servicesData={services} />
		case SECTION_REVIEWS:
			return <Reviews {...section} categoryWidgetId={categoryWidgetId} servicesData={services} />
		// case SECTION_SERVICES:
		// 	return <Services catTitle={catTitle} guid={guid} {...section} servicesData={services} />
		// case 'map':
		// 	return <InteractiveMap guid={guid} services={[services]} {...section} />
		case SECTION_WIDGET:
			return <Pricing {...section.widget} />
		default:
			return
	}
}

// Styled Components via Emotion
const SectionStyles = css`
	margin-bottom: 20px;
	width: 100%;
`
const SectionContainer = styled.article`
	${SectionStyles}
`
const Section = styled.section`
	${SectionStyles}
`
const SectionTitle = styled.h2`
	${({ theme }) => theme.header.h2};
	font-weight: 300;
	margin: 16px 0 8px 0;
`

Sections.propTypes = {
	catTitle: string,
	categoryWidgetId: string,
	guid: string,
	sections: array.isRequired,
	services: array,
}

export function Sections({ catTitle, categoryWidgetId, sections = [], services, guid }) {
	const isMegaBestPickOnly = [SECTION_BUYING_GUIDE, SECTION_MORECOVERAGE, SECTION_OURAPPROACH]
	const determineSectionStyle = (section) => {
		if (section.type === SECTION_CONTAINER) {
			// @todo: need to remove template logic after tests are complete
			if (section.container.isMegaBestPickOnly) {
				return
			}
			return SectionContainer
		} else {
			if (!isMegaBestPickOnly.includes(section.type)) {
				return Section
			}
			return
		}
	}

	return sections.map((section, i) => {
		// Based on the section type, container should always be wrapped i <article> else <section>
		const StyledSection = determineSectionStyle(section)

		if (StyledSection) {
			return (
				<StyledSection id={section.id} key={i}>
					<SectionTitle>{section.title}</SectionTitle>
					{renderSection({ catTitle, categoryWidgetId, section, services, guid })}
				</StyledSection>
			)
		}
		return null
	})
}
